import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header';
import Load from '../../../components/Load';
import { PageBreadcrumb } from '../../../components/navigation';
import { useProfile } from '../../../context/ProfileContext';
import { useLigaPermissions } from '../../../hooks/useLigaPermissions';
import { useCategoriaLiga } from '../../../hooks/useCategoriaLiga';
import CategoriaLigaItem from '../../../components/liga/categoriaLiga/CategoriaLigaItem';
import type { CategoriaLiga, CategoriaTorneioVinculada } from '../../../types/categoriaLiga';

/**
 * Catálogo CRUD de CategoriaLiga na área de gestão da Liga — mirror estrutural de
 * `TournamentDiscountsTab` (page chama hook/service; componentes recebem por props).
 * Criação/edição vivem em uma tela dedicada (`LeagueCategoriaLigaForm`) — os botões
 * "Nova Categoria" e "Editar" navegam para lá; o catálogo só lista/exclui/expande.
 *
 * PRPs/plans/categoria-liga-phase-3-frontend.plan.md — Task 5 (Satisfies AC-A1, AC-A4,
 * AC-A6).
 */
const LeagueCategoriaLigaCatalog: React.FC = () => {
  const navigate = useNavigate();
  const { currentProfile } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;
  const { canManageTournaments } = useLigaPermissions(ligaId);
  const { listar, deletar, listarInstancias } = useCategoriaLiga();

  const [items, setItems] = useState<CategoriaLiga[]>([]);
  const [loadingList, setLoadingList] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [instanciasPorCategoria, setInstanciasPorCategoria] = useState<
    Record<string, CategoriaTorneioVinculada[]>
  >({});

  const load = useCallback(async () => {
    if (!ligaId) {
      setItems([]);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setError(null);
    try {
      const data = await listar(ligaId);
      setItems(data);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar categorias da liga');
    } finally {
      setLoadingList(false);
    }
  }, [ligaId, listar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNovaCategoria = () => {
    navigate('/liga/categorias/nova');
  };

  const handleEditar = (categoria: CategoriaLiga) => {
    navigate(`/liga/categorias/${categoria.id}/editar`);
  };

  const handleExcluir = async (categoria: CategoriaLiga) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria da liga?')) return;
    try {
      await deletar(categoria.id);
      setItems((prev) => prev.filter((item) => item.id !== categoria.id));
    } catch (e: any) {
      setError(e?.message || 'Erro ao excluir categoria da liga');
    }
  };

  const handleVerEdicoesVinculadas = async (categoria: CategoriaLiga) => {
    if (expandedId === categoria.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(categoria.id);
    if (!instanciasPorCategoria[categoria.id]) {
      try {
        const instancias = await listarInstancias(categoria.id);
        setInstanciasPorCategoria((prev) => ({ ...prev, [categoria.id]: instancias }));
      } catch (e: any) {
        setError(e?.message || 'Erro ao buscar edições vinculadas');
      }
    }
  };

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <div className="mb-3">
                      <PageBreadcrumb
                        items={[
                          { label: 'Dashboard', href: '/liga/dashboard' },
                          { label: 'Categorias' },
                        ]}
                        onBack={() => navigate('/liga/dashboard')}
                        variant="bootstrap"
                      />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h3 className="fw-700 mb-0">Categorias da Liga</h3>
                      {canManageTournaments && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleNovaCategoria}
                        >
                          Nova Categoria
                        </button>
                      )}
                    </div>

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}

                    {loadingList ? (
                      <Load />
                    ) : items.length === 0 ? (
                      <div className="alert alert-info" role="alert">
                        Nenhuma categoria cadastrada para esta liga.
                      </div>
                    ) : (
                      <div className="row g-3">
                        {items.map((item) => (
                          <div key={item.id} className="col-12">
                            <CategoriaLigaItem
                              value={item}
                              onEdit={() => handleEditar(item)}
                              onDelete={() => handleExcluir(item)}
                              onVerEdicoesVinculadas={() => handleVerEdicoesVinculadas(item)}
                              edicoesVinculadas={
                                expandedId === item.id ? instanciasPorCategoria[item.id] : undefined
                              }
                              canEdit={canManageTournaments}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeagueCategoriaLigaCatalog;
